import { useCallback, useState } from 'react'

import { DndContext, DragEndEvent, useDraggable } from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import { useDevDrawerStore } from 'modules/desktop/pages/MainPage/stores'
import { createPortal } from 'react-dom'

import {
  Position,
  useClampPositionOnResize
} from '../../hooks/useClampPositionOnResize'
import { CpuPanel } from '../CpuPanel'
import { FpsPanel } from '../FpsPanel'
import { MemoryPanel } from '../MemoryPanel'
import styles from './DevDrawer.module.scss'

const STORAGE_KEY = 'dev-drawer-position'
const DRAWER_ID = 'dev-drawer'

const getInitialPosition = (): { x: number; y: number } => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      const isValid =
        typeof parsed.x === 'number' && typeof parsed.y === 'number'
      if (isValid) return parsed
    }
  } catch {
    // ignore
  }
  return { x: window.innerWidth - 200, y: window.innerHeight - 60 }
}

type DraggableDrawerProps = {
  basePosition: Position
  onPositionClamp: (pos: Position) => void
  children: React.ReactNode
}

const DraggableDrawer = ({
  basePosition,
  onPositionClamp,
  children
}: DraggableDrawerProps) => {
  const { attributes, listeners, setNodeRef, transform, node } = useDraggable({
    id: DRAWER_ID
  })

  useClampPositionOnResize({
    nodeRef: node,
    basePosition,
    onPositionClamp
  })

  return (
    <div
      ref={setNodeRef}
      className={styles.drawer}
      style={{
        left: basePosition.x + (transform?.x ?? 0),
        top: basePosition.y + (transform?.y ?? 0)
      }}
    >
      <span className={styles.handle} {...listeners} {...attributes} />
      {children}
    </div>
  )
}

export const DevDrawer = () => {
  const isVisible = useDevDrawerStore(state => state.isVisible)
  const hide = useDevDrawerStore(state => state.hide)
  const [memoryOpen, setMemoryOpen] = useState(false)
  const [fpsOpen, setFpsOpen] = useState(false)
  const [cpuOpen, setCpuOpen] = useState(false)
  const [basePosition, setBasePosition] = useState<Position>(getInitialPosition)

  const handleDragEnd = ({ delta }: DragEndEvent) => {
    setBasePosition(prev => {
      const next = { x: prev.x + delta.x, y: prev.y + delta.y }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // ignore
      }
      return next
    })
  }

  const handlePositionClamp = useCallback((pos: Position) => {
    setBasePosition(pos)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos))
    } catch {
      // ignore
    }
  }, [])

  if (!isVisible) return null

  return createPortal(
    <>
      {memoryOpen && <MemoryPanel onClose={() => setMemoryOpen(false)} />}
      {fpsOpen && <FpsPanel onClose={() => setFpsOpen(false)} />}
      {cpuOpen && <CpuPanel onClose={() => setCpuOpen(false)} />}

      <DndContext modifiers={[restrictToWindowEdges]} onDragEnd={handleDragEnd}>
        <DraggableDrawer
          basePosition={basePosition}
          onPositionClamp={handlePositionClamp}
        >
          <button
            className={styles.tool}
            onClick={() => setMemoryOpen(v => !v)}
            aria-label='Toggle memory panel'
            aria-pressed={memoryOpen}
          >
            <span className={styles.toolDot} data-active={memoryOpen} />
            Memory
          </button>

          <button
            className={styles.tool}
            onClick={() => setFpsOpen(v => !v)}
            aria-label='Toggle FPS panel'
            aria-pressed={fpsOpen}
          >
            <span className={styles.toolDot} data-active={fpsOpen} />
            FPS
          </button>

          <button
            className={styles.tool}
            onClick={() => setCpuOpen(v => !v)}
            aria-label='Toggle CPU panel'
            aria-pressed={cpuOpen}
          >
            <span className={styles.toolDot} data-active={cpuOpen} />
            CPU
          </button>

          <button
            className={styles.closeButton}
            onClick={hide}
            aria-label='Close dev drawer'
          >
            ×
          </button>
        </DraggableDrawer>
      </DndContext>
    </>,
    document.body
  )
}
